import moment from 'moment';
import { Concept } from '../types/concept.types';
import { Cycle } from '../types/cycle.types';
import { Participant } from '../types/participant.types';

const now = moment();

function cycleDuration(cycle?: Cycle): number {
  if (!cycle) {
    return;
  }

  const dateFrom = moment(cycle.dateFrom.date);
  const dateTo = moment(cycle.dateTo.date);
  const time1 = moment({ hour: dateFrom.hour(), minute: dateFrom.minute() });
  const time2 = moment({ hour: dateTo.hour(), minute: dateTo.minute() });
  const duration = moment.duration(time2.diff(time1));

  return duration.asMinutes();
}

function cycleStartTime(cycle?: Cycle): string {
  return cycle && moment(cycle.dateFrom.date).format('HH:mm');
}

function cycleDateRange(cycle?: Cycle): string {
  return (
    cycle &&
    [
      moment(cycle.dateFrom.date).format('DD.MM.YY'),
      moment(cycle.dateTo.date).format('DD.MM.YY'),
    ].join(' - ')
  );
}

function cycleTimeRange(cycle?: Cycle): string {
  return (
    cycle &&
    [cycleStartTime(cycle), moment(cycle.dateTo.date).format('HH:mm')].join(
      ' - '
    )
  );
}

function cycleDay(cycle?: Cycle, short: boolean = false): string {
  return cycle && moment(cycle.dateFrom.date).format(short ? 'dd' : 'dddd');
}

function isCycleFull(cycle?: Cycle, course?: Concept): boolean {
  return (
    cycle &&
    course &&
    (cycle.participants || []).length >= course.maxParticipants
  );
}

function isCycleStarted(cycle?: Cycle): boolean {
  return cycle?.dateFrom && moment(cycle.dateFrom.date).isBefore(moment());
}

function isCycleEnded(cycle?: Cycle): boolean {
  return cycle?.dateTo && moment(cycle.dateTo.date).isBefore(moment());
}

function isCycleRunning(cycle?: Cycle): boolean {
  return cycle && isCycleStarted(cycle) && !isCycleEnded(cycle);
}

function isInternalVisible(cycle?: Cycle, course?: Concept): boolean {
  if (!cycle || !course) {
    return false;
  }

  return (
    course.active &&
    cycle.active !== false &&
    !!cycle.dateFrom &&
    moment(cycle.dateFrom.date).isAfter(now, 'days') &&
    (!cycle.internPlanningDate ||
      moment(cycle.internPlanningDate).isSameOrBefore(now, 'days'))
  );
}

function isPublicVisible(cycle?: Cycle, course?: Concept): boolean {
  if (!cycle || !course) {
    return false;
  }

  return (
    course.active &&
    cycle.active !== false &&
    cycle.dateFrom &&
    (!cycle.publicPlanningDate ||
      moment(cycle.publicPlanningDate).isSameOrBefore(now, 'days'))
  );
}

function findParticipantByEmail(cycle?: Cycle, email?: string): Participant {
  if (!cycle || !email) {
    return;
  }

  return [...cycle.participants, ...cycle.waitList, ...cycle.banList].find(
    (participant) => participant.email === email
  );
}

function isOnParticipantList(cycle?: Cycle, participantId?: string): boolean {
  if (!cycle || !participantId) {
    return false;
  }

  return (cycle.participants || []).some(
    (participant) => participant.id === participantId
  );
}

function isOnWaitList(cycle?: Cycle, participantId?: string): boolean {
  if (!cycle || !participantId) {
    return false;
  }

  return (cycle.waitList || []).some(
    (participant) => participant.id === participantId
  );
}

function isOnBanList(cycle?: Cycle, participantId?: string) {
  if (!cycle || !participantId) {
    return false;
  }

  return (cycle.banList || []).some(
    (participant) => participant.id === participantId
  );
}

export {
  cycleStartTime,
  cycleDateRange,
  cycleTimeRange,
  cycleDuration,
  cycleDay,
  isCycleFull,
  isCycleStarted,
  isCycleEnded,
  isCycleRunning,
  isInternalVisible,
  isPublicVisible,
  findParticipantByEmail,
  isOnParticipantList,
  isOnWaitList,
  isOnBanList,
};
